/**
 * @engine/vfx — VFX preset builders (single-system group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Create a firework VFX graph with preset nodes.
 *
 * Includes:
 * - Burst spawn (50-100 particles)
 * - Random sphere position with small radius
 * - Cone velocity (upward spread)
 * - Gravity and drag
 * - Color over life (orange -> red -> fade)
 * - Size over life (shrink)
 * - Quad output
 */

export function createElectricStormVFX(
  name: string = "Electric Storm",
): VFXGraphData {
  const template: VFXGraphData = {
    id: "steam_electric_storm",
    name: "Electric Storm",
    nodes: [
      // Plasma system
      {
        id: "spawn_plasma",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 600 },
        contextId: "ctx_spawn_plasma",
      },
      {
        id: "init_plasma_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.3, max: 0.8 },
        contextId: "ctx_init_plasma",
      },
      {
        id: "init_plasma_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "sphere",
          radius: 2,
          radiusVariance: 0.5,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_plasma",
      },
      {
        id: "init_plasma_velocity",
        type: "vfx.init.velocity_from_center",
        position: { x: 50, y: 250 },
        inputValues: { center: { x: 0, y: 0, z: 0 }, speed: 8 },
        contextId: "ctx_init_plasma",
      },
      {
        id: "init_plasma_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.1, max: 0.25 },
        contextId: "ctx_init_plasma",
      },
      {
        id: "init_plasma_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 450 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 0.3, g: 0.6, b: 1, a: 1 } },
              { t: 0.5, color: { r: 0.5, g: 0.8, b: 1, a: 1 } },
              { t: 1, color: { r: 0.8, g: 0.95, b: 1, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init_plasma",
      },
      {
        id: "update_plasma_attract",
        type: "vfx.update.attract",
        position: { x: 50, y: 50 },
        inputValues: {
          target: { x: 0, y: 0, z: 0 },
          strength: 15,
          maxDistance: 5,
          falloff: 1,
        },
        contextId: "ctx_update_plasma",
      },
      {
        id: "update_plasma_turbulence",
        type: "vfx.update.turbulence",
        position: { x: 50, y: 150 },
        inputValues: { intensity: 8, frequency: 2, octaves: 3, roughness: 0.7 },
        contextId: "ctx_update_plasma",
      },
      {
        id: "update_plasma_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0 },
              { t: 0.1, v: 1 },
              { t: 0.8, v: 0.8 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_plasma",
      },
      {
        id: "output_plasma",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_plasma",
      },
      // Core system
      {
        id: "spawn_core",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 100 },
        contextId: "ctx_spawn_core",
      },
      {
        id: "init_core_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.5, max: 1 },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "sphere",
          radius: 0.5,
          radiusVariance: 0.2,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_velocity",
        type: "vfx.init.velocity_random",
        position: { x: 50, y: 250 },
        inputValues: { speed: 2, speedVariance: 1.5 },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.5, max: 1.5 },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 1, g: 1, b: 1, a: 1 } },
        contextId: "ctx_init_core",
      },
      {
        id: "update_core_curl",
        type: "vfx.update.curl_noise",
        position: { x: 50, y: 50 },
        inputValues: { intensity: 10, scale: 0.5, speed: 3 },
        contextId: "ctx_update_core",
      },
      {
        id: "update_core_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 150 },
        inputValues: { coefficient: 2 },
        contextId: "ctx_update_core",
      },
      {
        id: "update_core_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 1, g: 1, b: 1, a: 1 } },
              { t: 0.5, color: { r: 0.5, g: 0.8, b: 1, a: 1 } },
              { t: 1, color: { r: 0.3, g: 0.5, b: 1, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update_core",
      },
      {
        id: "update_core_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0 },
              { t: 0.2, v: 1 },
              { t: 0.8, v: 0.8 },
              { t: 1, v: 0 },
            ],
          },
          scale: 2,
        },
        contextId: "ctx_update_core",
      },
      {
        id: "output_core",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_core",
      },
      // Burst system
      {
        id: "spawn_burst",
        type: "vfx.spawn.periodic",
        position: { x: 50, y: 50 },
        inputValues: { count: 50, period: 0.3 },
        contextId: "ctx_spawn_burst",
      },
      {
        id: "init_burst_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.15, max: 0.3 },
        contextId: "ctx_init_burst",
      },
      {
        id: "init_burst_velocity",
        type: "vfx.init.velocity_random",
        position: { x: 50, y: 150 },
        inputValues: { speed: 20, speedVariance: 10 },
        contextId: "ctx_init_burst",
      },
      {
        id: "init_burst_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 250 },
        inputValues: { min: 0.02, max: 0.08 },
        contextId: "ctx_init_burst",
      },
      {
        id: "init_burst_color",
        type: "vfx.init.color",
        position: { x: 50, y: 350 },
        inputValues: { color: { r: 0.8, g: 0.95, b: 1, a: 1 } },
        contextId: "ctx_init_burst",
      },
      {
        id: "update_burst_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 50 },
        inputValues: { coefficient: 8 },
        contextId: "ctx_update_burst",
      },
      {
        id: "update_burst_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 1 },
              { t: 0.5, v: 0.8 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_burst",
      },
      {
        id: "output_burst",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_burst",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      {
        id: "ctx_spawn_plasma",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_plasma"],
        collapsed: false,
      },
      {
        id: "ctx_init_plasma",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_plasma_lifetime",
          "init_plasma_position",
          "init_plasma_velocity",
          "init_plasma_size",
          "init_plasma_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_plasma",
        type: "vfx.update",
        position: { x: 50, y: 750 },
        size: { width: 280, height: 300 },
        nodeIds: [
          "update_plasma_attract",
          "update_plasma_turbulence",
          "update_plasma_alpha",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_plasma",
        type: "vfx.output",
        position: { x: 50, y: 1100 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_plasma"],
        collapsed: false,
      },
      {
        id: "ctx_spawn_core",
        type: "vfx.spawn",
        position: { x: 400, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_core"],
        collapsed: false,
      },
      {
        id: "ctx_init_core",
        type: "vfx.initialize",
        position: { x: 400, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_core_lifetime",
          "init_core_position",
          "init_core_velocity",
          "init_core_size",
          "init_core_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_core",
        type: "vfx.update",
        position: { x: 400, y: 750 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "update_core_curl",
          "update_core_drag",
          "update_core_color",
          "update_core_size",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_core",
        type: "vfx.output",
        position: { x: 400, y: 1200 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_core"],
        collapsed: false,
      },
      {
        id: "ctx_spawn_burst",
        type: "vfx.spawn",
        position: { x: 750, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_burst"],
        collapsed: false,
      },
      {
        id: "ctx_init_burst",
        type: "vfx.initialize",
        position: { x: 750, y: 200 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "init_burst_lifetime",
          "init_burst_velocity",
          "init_burst_size",
          "init_burst_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_burst",
        type: "vfx.update",
        position: { x: 750, y: 650 },
        size: { width: 280, height: 200 },
        nodeIds: ["update_burst_drag", "update_burst_alpha"],
        collapsed: false,
      },
      {
        id: "ctx_output_burst",
        type: "vfx.output",
        position: { x: 750, y: 900 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_burst"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      {
        id: "flow_plasma_1",
        fromContextId: "ctx_spawn_plasma",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_plasma",
        toPortId: "spawn_event",
      },
      {
        id: "flow_plasma_2",
        fromContextId: "ctx_init_plasma",
        fromPortId: "particle",
        toContextId: "ctx_update_plasma",
        toPortId: "particle",
      },
      {
        id: "flow_plasma_3",
        fromContextId: "ctx_update_plasma",
        fromPortId: "particle",
        toContextId: "ctx_output_plasma",
        toPortId: "particle",
      },
      {
        id: "flow_core_1",
        fromContextId: "ctx_spawn_core",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_core",
        toPortId: "spawn_event",
      },
      {
        id: "flow_core_2",
        fromContextId: "ctx_init_core",
        fromPortId: "particle",
        toContextId: "ctx_update_core",
        toPortId: "particle",
      },
      {
        id: "flow_core_3",
        fromContextId: "ctx_update_core",
        fromPortId: "particle",
        toContextId: "ctx_output_core",
        toPortId: "particle",
      },
      {
        id: "flow_burst_1",
        fromContextId: "ctx_spawn_burst",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_burst",
        toPortId: "spawn_event",
      },
      {
        id: "flow_burst_2",
        fromContextId: "ctx_init_burst",
        fromPortId: "particle",
        toContextId: "ctx_update_burst",
        toPortId: "particle",
      },
      {
        id: "flow_burst_3",
        fromContextId: "ctx_update_burst",
        fromPortId: "particle",
        toContextId: "ctx_output_burst",
        toPortId: "particle",
      },
    ],
    contextSettings: {
      ctx_init_plasma: { capacity: 2000, boundsMode: "auto" },
      ctx_init_core: { capacity: 500, boundsMode: "auto" },
      ctx_init_burst: { capacity: 1000, boundsMode: "auto" },
    },
    capacity: { maxParticles: 4000, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "none", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}

/**
 * Magic Portal - Swirling vortex with ring particles, core glow, and sparkles.
 * Multi-system with vortex motion and attraction.
 */
